import React, { useMemo, useState, useEffect, useRef } from "react";
import { 
  Container, 
  Button, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Card,
  CardBody,
  Badge,
  Row,
  Col,
  Alert,
  Spinner
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { fetchTransactionsHistoryRequest } from "../../store/transactionHistory/actions";
import TableContainer from "components/Common/TableContainer";
import { format, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';
import { FiExternalLink, FiClock, FiUser, FiCreditCard, FiDollarSign, FiDownload } from "react-icons/fi";
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRCode from 'react-qr-code';

const TransactionsHistory = () => {
  const dispatch = useDispatch();
  const { transactions, loading, error } = useSelector((state) => state.transactionHistory);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    dispatch(fetchTransactionsHistoryRequest());
  }, [dispatch]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const viewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    toggleModal();
  };

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'تاريخ غير صالح';
    }
  };

  const PaymentStatusBadge = ({ status }) => {
    const statusMap = {
      paid: { color: 'success', text: 'مدفوعة' },
      completed: { color: 'success', text: 'مكتمل' },
      pending: { color: 'warning', text: 'قيد الانتظار' },
      failed: { color: 'danger', text: 'فشل' },
      refunded: { color: 'info', text: 'تم الاسترجاع' }
    };

    const currentStatus = statusMap[status] || { color: 'secondary', text: status };
    
    return (
      <Badge color={currentStatus.color} className="p-2">
        {currentStatus.text}
      </Badge>
    );
  };

  const DeductionStatusBadge = ({ status }) => {
    const normalizedStatus = String(status || "").toLowerCase();
    const statusMap = {
      deducted: { color: 'success', text: 'مخصوم' },
      pending_settlement: { color: 'info', text: 'معلق للتسوية القادمة' },
      partial: { color: 'warning', text: 'خصم جزئي' },
      pending: { color: 'warning', text: 'قيد الانتظار' },
      failed: { color: 'danger', text: 'فشل' },
    };

    const currentStatus = statusMap[normalizedStatus];
    if (!currentStatus) return <span className="text-muted">--</span>;

    return (
      <Badge color={currentStatus.color} className="p-2">
        {currentStatus.text}
      </Badge>
    );
  };

  const hasFineContext = (item) => {
    const fineAmount = Number(item?.fine_deductions_total || 0);
    const status = String(item?.deduction_status || "").toLowerCase();
    const chargedTo = String(item?.fine_charged_to || "").toLowerCase();

    return (
      fineAmount > 0 ||
      ["pending_settlement", "partial", "deducted", "pending"].includes(status) ||
      ["provider", "app_user"].includes(chargedTo)
    );
  };

  const ChargedToBadge = ({ chargedTo, hasFine }) => {
    if (!hasFine) {
      return <span className="text-muted">--</span>;
    }

    const normalized = String(chargedTo || "").toLowerCase();

    if (normalized === "provider") {
      return <Badge color="warning" className="p-2">على مقدم الخدمة</Badge>;
    }

    if (normalized === "app_user") {
      return <Badge color="info" className="p-2">على العميل</Badge>;
    }

    return <span className="text-muted">--</span>;
  };

  const groupedTransactions = useMemo(() => {
    const list = Array.isArray(transactions) ? transactions : [];
    const groups = new Map();

    const toDate = (value) => {
      try {
        if (!value) return new Date(0);
        return typeof value === 'string' ? new Date(value) : new Date(value);
      } catch {
        return new Date(0);
      }
    };

    const toNumber = (value) => Number(value || 0);

    list.forEach((tx, index) => {
      const batchId = tx?.batch_settlement_id;

      if (batchId) {
        const key = `batch-${batchId}`;
        const current = groups.get(key);
        const txDate = toDate(tx.transaction_date || tx.created_at);

        if (!current) {
          groups.set(key, {
            ...tx,
            service_name: "تسوية مجمعة",
            is_batched: true,
            invoice_count: tx.invoice_count || 1,
            invoice_ids: Array.isArray(tx.invoice_ids) ? tx.invoice_ids : [],
            total_platform_revenue: toNumber(tx.total_platform_revenue),
            total_after_discount: toNumber(tx.total_after_discount),
            app_commission: toNumber(tx.app_commission),
            service_provider_commission: toNumber(tx.service_provider_commission),
            total_commission: toNumber(tx.total_commission),
            total_commission_vat: toNumber(tx.total_commission_vat),
            fine_deductions_total: toNumber(tx.fine_deductions_total),
            deduction_status: tx.deduction_status || null,
            fine_charged_to: tx.fine_charged_to || null,
            refund_status: tx.refund_status || null,
            needs_refund: tx.needs_refund === true,
            transaction_date: tx.transaction_date || tx.created_at,
            _date: txDate,
          });
        } else {
          current.total_platform_revenue += toNumber(tx.total_platform_revenue);
          current.total_after_discount += toNumber(tx.total_after_discount);
          current.app_commission += toNumber(tx.app_commission);
          current.service_provider_commission += toNumber(tx.service_provider_commission);
          current.total_commission += toNumber(tx.total_commission);
          current.total_commission_vat += toNumber(tx.total_commission_vat);
          current.fine_deductions_total += toNumber(tx.fine_deductions_total);

          const currentStatus = String(current.deduction_status || "").toLowerCase();
          const incomingStatus = String(tx.deduction_status || "").toLowerCase();
          if (incomingStatus === "pending_settlement") {
            current.deduction_status = "pending_settlement";
          } else if (!currentStatus && incomingStatus) {
            current.deduction_status = incomingStatus;
          }

          if (!current.fine_charged_to && tx.fine_charged_to) {
            current.fine_charged_to = tx.fine_charged_to;
          }

          if (!current.refund_status && tx.refund_status) {
            current.refund_status = tx.refund_status;
          }

          if (!current.needs_refund && tx.needs_refund === true) {
            current.needs_refund = true;
          }

          current.invoice_count = (current.invoice_count || 0) + (tx.invoice_count || 1);
          if (Array.isArray(tx.invoice_ids)) {
            current.invoice_ids = Array.from(new Set([...(current.invoice_ids || []), ...tx.invoice_ids]));
          }
          if (txDate > (current._date || new Date(0))) {
            current._date = txDate;
            current.transaction_date = tx.transaction_date || tx.created_at;
          }
          groups.set(key, current);
        }
      } else {
        const key = `single-${tx?.id || tx?.invoice_id || index}`;
        groups.set(key, tx);
      }
    });

    return Array.from(groups.values()).map(({ _date, ...rest }) => rest);
  }, [transactions]);

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "index",
        Cell: ({ row }) => row.index + 1,
        width: 50
      },
      {
        Header: "اسم الخدمة",
        accessor: "service_name",
        Cell: ({ value }) => value || "غير متوفر",
      },
      {
        Header: "مقدم الخدمة",
        accessor: "service_provider_name",
        Cell: ({ value }) => value || "غير متوفر",
      },
      {
        Header: "المبلغ",
        accessor: "total_platform_revenue",
        Cell: ({ value }) => `${value || 0} ر.س`,
      },
      {
        Header: "حالة الدفع",
        accessor: "status",
        Cell: ({ value }) => <PaymentStatusBadge status={value} />,
      },
      // {
      //   Header: "حالة الخصم",
      //   accessor: "deduction_status",
      //   Cell: ({ value }) => <DeductionStatusBadge status={value} />,
      // },
      // {
      //   Header: "تحميل الغرامة",
      //   accessor: "fine_charged_to",
      //   Cell: ({ value, row }) => <ChargedToBadge chargedTo={value} hasFine={hasFineContext(row.original)} />,
      // },
      {
        Header: "التاريخ",
        accessor: "transaction_date",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "التفاصيل",
        accessor: "actions",
        Cell: ({ row }) => (
          <Button 
            color="primary" 
            size="sm" 
            onClick={() => viewDetails(row.original)}
            className="d-flex align-items-center"
          >
            <FiExternalLink className="me-1" />
            عرض
          </Button>
        ),
        width: 100
      },
    ],
    []
  );

  return (
    <div className="page-content" dir="rtl">
      <Container fluid>
        <Breadcrumbs 
          title="أرشيف المعاملات" 
          breadcrumbItem="سجل الفواتير المالية" 
        />
        
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">جاري تحميل البيانات...</p>
          </div>
        ) : error ? (
          <Alert color="danger">
            <h5 className="alert-heading">حدث خطأ!</h5>
            <p>{error}</p>
            <Button color="danger" size="sm" onClick={() => dispatch(fetchTransactionsHistoryRequest())}>
              إعادة المحاولة
            </Button>
          </Alert>
        ) : (
          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={groupedTransactions || []}
                isGlobalFilter={true}
                isAddOptions={false}
                customPageSize={10}
                className="custom-header-css"
              />
            </CardBody>
          </Card>
        )}

        <TransactionDetailsModal 
          isOpen={isModalOpen} 
          toggle={toggleModal} 
          transaction={selectedTransaction} 
        />
      </Container>
    </div>
  );
};



const TransactionDetailsModal = ({ isOpen, toggle, transaction }) => {
  // All hooks must be called unconditionally at the top
  const [erpInvoice, setErpInvoice] = useState(null);
  const [loadingERP, setLoadingERP] = useState(false);
  const invoiceRef = useRef();
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundResponse, setRefundResponse] = useState(null);
  const [refundError, setRefundError] = useState(null);

  const toNumber = (value) => Number(value || 0);
  const erpGrandTotal = toNumber(erpInvoice?.[0]?.grand_total ?? erpInvoice?.grand_total ?? transaction?.total_platform_revenue ?? 0);
  const totalCommissionVat = toNumber(transaction?.total_commission_vat);
  const totalRevenueExVat = Math.max(erpGrandTotal - totalCommissionVat, 0);
  const totalAppCommission = toNumber(transaction?.app_commission);
  const totalServiceProviderCommission = toNumber(transaction?.service_provider_commission);
  const totalCommission = toNumber(transaction?.total_commission);
  const totalPlatformRevenue = toNumber(transaction?.total_platform_revenue ?? erpGrandTotal);
  const deductionStatus = String(transaction?.deduction_status || "").toLowerCase();
  const fineChargedTo = String(transaction?.fine_charged_to || "").toLowerCase();
  const refundStatus = String(transaction?.refund_status || "").toLowerCase();
  const hasDelayKeyword = (value) => {
    const normalized = String(value || "").toLowerCase();
    return normalized.includes("delay") || normalized.includes("provider_delay") || normalized.includes("تأخير");
  };
  const isDelayFine =
    String(transaction?.fine_type || "").toLowerCase() === "provider_delay" ||
    String(transaction?.fine_type || "").toLowerCase() === "mixed_delay_cancellation" ||
    hasDelayKeyword(transaction?.fine_reason) ||
    (Array.isArray(transaction?.deductions) &&
      transaction.deductions.some((item) => hasDelayKeyword(item?.type) || hasDelayKeyword(item?.reason)));
  const fineTypeLabel = isDelayFine ? "غرامة تأخير" : "غرامة إلغاء";
  const originalServiceAmount = toNumber(transaction?.original_service_amount ?? transaction?.total_after_discount);
  const platformCommissionAmount = toNumber(
    transaction?.platform_commission ?? (totalCommission + totalCommissionVat)
  );
  const netWithVatAmount = toNumber(transaction?.net_amount_with_vat ?? transaction?.net_after_fines);
  const finalPayoutAmount = toNumber(
    transaction?.final_payout ?? transaction?.net_payout ?? transaction?.net_after_fines
  );
  const fineAmount = toNumber(
    transaction?.cancellation_fines ?? transaction?.fine_deductions_total
  );
  const hasFineContextForModal =
    toNumber(transaction?.fine_deductions_total) > 0 ||
    ["pending_settlement", "partial", "deducted", "pending"].includes(deductionStatus) ||
    ["provider", "app_user"].includes(fineChargedTo);
  const isRefundMarked =
    transaction?.is_refund === true ||
    transaction?.needs_refund === true ||
    (refundStatus !== "" && !["none", "not_required", "no_refund"].includes(refundStatus));

  const fetchERPInvoice = async (invoiceId) => {
    try {
      setLoadingERP(true);
      // Fetch commission ERP invoice (not service invoice)


      const response = await fetch(
        `https://node-server-eta-coral.vercel.app/invoice-details/${encodeURIComponent(invoiceId)}`,
        {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_NODE_API_KEY
        }
        }
      );
      const json = await response.json();

      setErpInvoice(json.data);
    } catch (err) {
      console.error("Error fetching commission ERP invoice:", err);
    } finally {
      setLoadingERP(false);
    }
  };

  const fetchPrivateImage = async (filePath) => {
    const response = await fetch(
      `https://node-server-eta-coral.vercel.app/erp-image?path=${encodeURIComponent(filePath)}`
    );
    if (!response.ok) throw new Error("Failed to fetch image");
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };

  useEffect(() => {
    if (!isOpen) return;
    // Use erp_invoice_id to fetch our commission invoice
    const targetId = transaction?.erp_invoice_id || null;
    
    fetchERPInvoice(targetId);
  }, [isOpen, transaction?.erp_invoice_id]);

  useEffect(() => {
      if (!erpInvoice?.[0]?.ksa_einv_qr) return;
  
      setLoadingQR(true);
      fetchPrivateImage(erpInvoice[0].ksa_einv_qr)
        .then(setQrImageUrl)
        .catch(console.error)
        .finally(() => setLoadingQR(false));
  
      return () => {
        if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
      };
    }, [erpInvoice?.[0]?.ksa_einv_qr]);


  const handlePrint = useReactToPrint({
    content: () => invoiceRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          direction: rtl;
        }
        .invoice-header {
          border-bottom: 2px solid #000;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        .invoice-title {
          color: #333;
        }
        .invoice-details {
          background-color: #f9f9f9;
          padding: 15px;
          border-radius: 5px;
        }
      }
    `,
  });

  


  // Helper functions can be defined after hooks
  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'تاريخ غير صالح';
    }
  };

  const downloadPDF = () => {
    if (!transaction) return; // Add safety check
    
    const input = invoiceRef.current;
    
    html2canvas(input, {
      scale: 2,
      logging: true,
      useCORS: true
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`invoice_${transaction.id?.slice(0, 8) || 'N/A'}.pdf`);
    });
  };

 // Generate QR code data string
 const generateQRData = () => {
  if (!transaction) return '';

  return JSON.stringify({
    invoiceId: transaction.id,
    amount: transaction.amount_paid,
    date: transaction.transaction_date,
    status: transaction.status,
    service: transaction.service_name,
    provider: transaction.service_provider_name
  });
};

  const parseServerMessages = (msgStr) => {
    try {
      const arr = JSON.parse(msgStr);
      return arr
        .map(m => {
          try { return JSON.parse(m)?.message || m; } catch { return m; }
        })
        .join(" | ");
    } catch {
      return msgStr;
    }
  };

  const createCreditNote = async () => {
    if (!erpInvoice?.name || !erpInvoice?.customer || !erpInvoice?.company) {
      setRefundError("بيانات الفاتورة غير مكتملة لإنشاء إشعار دائن.");
      return;
    }
    setRefundError(null);
    setRefundResponse(null);
    setRefunding(true);

    const today = format(new Date(), "yyyy-MM-dd");
    const grandTotal = Number(erpInvoice?.grand_total ?? 0);

    const itemsPayload = Array.isArray(erpInvoice?.items)
      ? erpInvoice.items.map(it => ({
          item_code: it.item_code,
          qty: -(Number(it.qty ?? 1)),
          rate: Number(it.rate ?? 0)
        }))
      : [];

    const paymentsPayload = Array.isArray(erpInvoice?.payments) && erpInvoice.payments.length > 0
      ? [{
          mode_of_payment: erpInvoice.payments[0].mode_of_payment || "Cash",
          amount: -grandTotal,
          base_amount: -grandTotal,
          account: erpInvoice.payments[0].account || "1110 - Cash - DS"
        }]
      : [];

    const payload = {
      customer: erpInvoice.customer,
      is_return: 1,
      docstatus: 1,
      company: erpInvoice.company,
      return_against: erpInvoice.name,
      update_outstanding_for_self: 0,
      is_pos: erpInvoice.is_pos ? 1 : 0,
      cost_center: erpInvoice.cost_center || "Main - DS",
      posting_date: today,
      due_date: today,
      payments: paymentsPayload,
      items: itemsPayload
    };

    try {
      const res = await fetch("https://node-server-eta-coral.vercel.app/invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json"
          , 'x-api-key': process.env.REACT_APP_NODE_API_KEY
         },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (!res.ok || json?.error) {
        const serverMsg = json?._server_messages
          ? parseServerMessages(json._server_messages)
          : (json?.message || json?.error || "فشل إنشاء إشعار دائن.");
        setRefundError(serverMsg);
      } else {
        setRefundResponse(json?.data || json);
        // Close modal after 2 seconds
        setTimeout(() => {
          toggle();
        }, 2000);
      }
    } catch (e) {
      setRefundError(`خطأ في الاتصال: ${e.message}`);
    } finally {
      setRefunding(false);
    }
  };

  // Now we can check for transaction after all hooks
  if (!transaction) return null;

  const SkeletonBlock = ({ height = 16, width = "100%" }) => (
    <div
      style={{
        background: "#f0f0f0",
        height,
        width,
        borderRadius: 6,
        marginBottom: 8,
        animation: "pulse 1.2s ease-in-out infinite",
      }}
    />
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="border-0">
        <span className="modal-title">فاتورة العمولة - Commission Invoice</span>
      </ModalHeader>
      <ModalBody>
        {loadingERP ? (
          <div className="p-3">
            <div className="d-flex justify-content-between mb-3">
              <SkeletonBlock width="40%" height={18} />
              <SkeletonBlock width="30%" height={18} />
            </div>
            <SkeletonBlock height={20} width="60%" />
            <SkeletonBlock height={20} width="50%" />
            <SkeletonBlock height={20} width="70%" />
            <div className="d-flex gap-3 mt-3">
              <SkeletonBlock height={100} width="50%" />
              <SkeletonBlock height={100} width="50%" />
            </div>
            <div className="mt-3">
              <SkeletonBlock height={18} width="30%" />
              {[...Array(3)].map((_, i) => (
                <SkeletonBlock key={i} height={14} width={`${70 - i * 10}%`} />
              ))}
            </div>
            <style>{`
              @keyframes pulse {
                0% { opacity: 1; }
                50% { opacity: 0.4; }
                100% { opacity: 1; }
              }
            `}</style>
          </div>
        ) : (
          <div className="invoice-container" ref={invoiceRef}>
          <div className="invoice-header text-center mb-4">
            <h2 className="invoice-title">فاتورة العمولة</h2>
            <p className="text-muted">Commission Invoice - فاتورة إلكترونية</p>
          </div>

          <img 
            src="/logo.png"
            alt="Company Logo" 
            style={{ height: 60, objectFit: "contain" }} 
            className="mb-4 d-block mx-auto"
          />

          {/* From / To Header with platform (super-admin) details */}
          <div className="from-to-header mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <Row className="align-items-start">
              <Col md={6} className="text-end">
                <h6 className="mb-2"><strong>من - الجهة المُصدرة:</strong></h6>
                <p className="mb-1"><strong>الاسم:</strong> {transaction?.platform?.company_name || transaction?.platform?.name || 'ﺷﺮﻛﺔ ﺍﻟﻔﻜﺮﺓ ﺍﻹﺑﺪﺍﻋﻴﻪ ﺍﻟﺘﺠﺎﺭﻳﺔ'}</p>
                <p className="mb-1"><strong>السجل التجاري:</strong> {transaction?.platform?.commercial_register || transaction?.platform?.commercialRegister || 'غير متوفر'}</p>
                <p className="mb-1"><strong>رقم الضريبة:</strong> {transaction?.platform?.vat_number || transaction?.platform?.vatNumber || 'غير متوفر'}</p>
                <p className="mb-1"><strong>البلد:</strong> {transaction?.platform?.country || 'غير متوفر'}</p>
                <p className="mb-0"><strong>العنوان:</strong> {transaction?.platform?.address || 'غير متوفر'}</p>
              </Col>

              <Col md={1} className="text-center d-flex align-items-center justify-content-center">
                <span style={{ fontSize: '20px' }}>←</span>
              </Col>

              <Col md={5}>
                <h6 className="mb-2"><strong>إلى - مقدم الخدمة:</strong></h6>
                <p className="mb-1"><strong>الاسم:</strong> {transaction?.service_provider_name || 'غير متوفر'}</p>
                <p className="mb-1"><strong>الجوال:</strong> {transaction?.service_provider_mobile || 'غير متوفر'}</p>
                <p className="mb-1"><strong>رقم الضريبة:</strong> {transaction?.service_provider_vat_number || 'غير متوفر'}</p>
                <p className="mb-0"><strong>العنوان:</strong> {transaction?.service_provider_address || 'غير متوفر'}</p>
              </Col>
            </Row>
          </div>
          
          <Row className="mb-4">
            {/* <Col md={6}>
              <div className="provider-info mb-3" style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px' }}>
                <h5 className="mb-3">بيانات مقدم الخدمة</h5>
                <p className="mb-1"><strong>الاسم:</strong> {transaction.service_provider_name || 'غير متوفر'}</p>
                <p className="mb-1"><strong>رقم الجوال:</strong> {transaction.service_provider_mobile || 'غير متوفر'}</p>
                <p className="mb-1"><strong>رقم الضريبة:</strong> {transaction.service_provider_vat_number || 'غير متوفر'}</p>
                <p className="mb-1"><strong>السجل التجاري:</strong> {transaction.service_provider_commercial_register || 'غير متوفر'}</p>
                <p className="mb-0"><strong>العنوان:</strong> {transaction.service_provider_address || 'غير متوفر'}</p>
              </div>
            </Col> */}
            <Col className="text-end">
              <div className="invoice-info mb-3" style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px' }}>
                <h5 className="mb-3">معلومات فاتورة العمولة</h5>
                <p className="mb-1"><strong>رقم الفاتورة:</strong> {erpInvoice?.[0]?.name || "N/A"}</p>
                <p className="mb-1"><strong>تاريخ الفاتورة:</strong> {formatDate(erpInvoice?.[0]?.posting_date)}</p>
                <p className="mb-1"><strong>الخدمة:</strong> {transaction.service_name || 'غير متوفر'}</p>
                <p className="mb-0"><strong>حالة الفاتورة:</strong> 
                  <Badge color={
                    transaction.status === 'paid' ? 'success' : 
                    transaction.status === 'pending' ? 'warning' : 'secondary'
                  } className="ms-2">
                    {transaction.status === 'paid' ? 'مدفوعة' : 
                     transaction.status === 'pending' ? 'قيد الانتظار' : transaction.status}
                  </Badge>
                </p>
                <p className="mb-0 mt-2"><strong>حالة خصم الغرامة:</strong>
                  <Badge
                    color={
                      deductionStatus === "deducted"
                        ? "success"
                        : deductionStatus === "pending_settlement"
                        ? "info"
                        : deductionStatus === "partial"
                        ? "warning"
                        : "secondary"
                    }
                    className="ms-2"
                  >
                    {deductionStatus === "deducted"
                      ? "مخصوم"
                      : deductionStatus === "pending_settlement"
                      ? "معلق للتسوية القادمة"
                      : deductionStatus === "partial"
                      ? "خصم جزئي"
                      : "غير محدد"}
                  </Badge>
                </p>
                {hasFineContextForModal && (
                  <>
                    <p className="mb-0 mt-2"><strong>نوع الغرامة:</strong>
                      <Badge
                        color={isDelayFine ? "danger" : "warning"}
                        className="ms-2"
                      >
                        {fineTypeLabel}
                      </Badge>
                    </p>
                    <p className="mb-0 mt-2"><strong>تحميل الغرامة:</strong>
                      <Badge
                        color={fineChargedTo === "provider" ? "warning" : fineChargedTo === "app_user" ? "info" : "secondary"}
                        className="ms-2"
                      >
                        {fineChargedTo === "provider"
                          ? "على مقدم الخدمة"
                          : fineChargedTo === "app_user"
                          ? "على العميل"
                          : "غير محدد"}
                      </Badge>
                    </p>
                  </>
                )}
                {isRefundMarked && (
                  <p className="mb-0 mt-2"><strong>الاسترجاع:</strong>
                    <Badge color="danger" className="ms-2">
                      {refundStatus === "pending" ? "فاتورة مميزة للاسترجاع (قيد التنفيذ)" : "فاتورة مميزة للاسترجاع"}
                    </Badge>
                  </p>
                )}
              </div>
            </Col>
          </Row>

          <div className="invoice-details mb-4">
            <h5 className="mb-3">ملخص التسوية</h5>
            <table className="table table-sm table-bordered">
              <tbody>
                <tr>
                  <td>قيمة الخدمة الأصلية</td>
                  <td className="text-end">{originalServiceAmount.toFixed(2)} ر.س</td>
                </tr>
                <tr>
                  <td>إجمالي عمولة المنصة (مع الضريبة)</td>
                  <td className="text-end">{platformCommissionAmount.toFixed(2)} ر.س</td>
                </tr>
                <tr>
                  <td>الصافي بعد العمولة + الضريبة</td>
                  <td className="text-end">{netWithVatAmount.toFixed(2)} ر.س</td>
                </tr>
                <tr className={fineAmount > 0 ? "table-warning" : ""}>
                  <td>{fineTypeLabel}</td>
                  <td className="text-end">{fineAmount.toFixed(2)} ر.س</td>
                </tr>
                <tr className="table-active">
                  <td><strong>المبلغ النهائي لمقدم الخدمة</strong></td>
                  <td className="text-end"><strong>{finalPayoutAmount.toFixed(2)} ر.س</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="invoice-details mb-4">
            <h5 className="mb-3">تفاصيل المعاملة</h5>
            <Row>
              <Col md={6}>
                <p><strong>الخدمة المقدمة:</strong> {transaction.service_name || 'غير متوفر'}</p>
                <p><strong>قيمة الخدمة الإجمالية:</strong> {toNumber(transaction?.total_after_discount).toFixed(2)} ر.س</p>
              </Col>
              <Col md={6} className="d-flex justify-content-end">
                <div className="qr-code-container" style={{ 
                  backgroundColor: 'white', 
                  padding: '10px',
                  border: '1px solid #eee',
                  borderRadius: '4px'
                }}>
                  {loadingQR ? (
                    <div className="d-flex align-items-center justify-content-center" style={{ width: 128, height: 128 }}>
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : qrImageUrl ? (
                    <img
                      src={qrImageUrl}
                      alt="ZATCA QR Code"
                      style={{ width: 128, height: 128, objectFit: "contain" }}
                    />
                  ) : (
                    <Alert color="info" className="p-2 m-0" />
                  )}
                </div>
              </Col>
            </Row>
          </div>
          
          <div className="invoice-summary">
            <h5 className="mb-3">ملخص فاتورة العمولة</h5>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>{erpInvoice?.[0]?.company_name_on_invoice || erpInvoice?.[0]?.company || 'ﺷﺮﻛﺔ ﺍﻟﻔﻜﺮﺓ ﺍﻹﺑﺪﺍﻋﻴﻪ ﺍﻟﺘﺠﺎﺭﻳﺔ'}</th>
                  <th className="text-end">المبلغ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>عمولة التطبيق</td>
                  <td className="text-end">{totalAppCommission.toFixed(2)} ر.س</td>
                </tr>
                <tr>
                  <td>عمولة مقدم الخدمة</td>
                  <td className="text-end">{totalServiceProviderCommission.toFixed(2)} ر.س</td>
                </tr>
                <tr>
                  <td>إجمالي العمولات (قبل الضريبة)</td>
                  <td className="text-end">{totalCommission.toFixed(2)} ر.س</td>
                </tr>
                <tr>
                  <td>ضريبة القيمة المضافة (15%)</td>
                  <td className="text-end">{totalCommissionVat.toFixed(2)} ر.س</td>
                </tr>
                {toNumber(transaction?.fine_deductions_total) > 0 && (
                  <tr className="table-warning">
                    <td>
                      <strong>{fineTypeLabel}</strong>
                      <div className="text-muted" style={{ fontSize: '0.85em' }}>
                        {fineChargedTo === "provider"
                          ? "تحميل الغرامة: مقدم الخدمة"
                          : fineChargedTo === "app_user"
                          ? "تحميل الغرامة: العميل"
                          : "تحميل الغرامة: غير محدد"}
                      </div>
                      {transaction.fine_reason && (
                        <div className="text-muted" style={{ fontSize: '0.85em' }}>
                          {transaction.fine_reason}
                        </div>
                      )}
                    </td>
                    <td className="text-end text-danger"><strong>{toNumber(transaction?.fine_deductions_total).toFixed(2)} ر.س</strong></td>
                  </tr>
                )}
                {/* <tr>
                  <td>رقم الضريبة</td>
                  <td className="text-end">{erpInvoice?.company_tax_id || '311376152500003'}</td>
                </tr> */}
                  {/* <tr className="table-active">
                  <td><strong>إجمالي        </strong></td>
                  <td className="text-end"><strong>{totalRevenueExVat.toFixed(2)} ر.س</strong></td>
                </tr> */}
                <tr className="table-active">
                  <td><strong>إجمالي       مع ضريبة</strong></td>
                  <td className="text-end"><strong>{totalPlatformRevenue.toFixed(2)} ر.س</strong></td>
                </tr>

                
              </tbody>
            </table>
          </div>
          
          {/* ERP details section */}
          <div className="invoice-details mb-4">

            {Array.isArray(erpInvoice?.items) && erpInvoice.items.length > 0 && (
              <div className="mt-3">
                <h6 className="mb-2">المنتجات/الخدمات</h6>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>الاسم</th>
                      <th>الكمية</th>
                      <th className="text-end">السعر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {erpInvoice.items.map((it) => (
                      <tr key={it.name}>
                        <td>{it.item_name || it.description || '—'}</td>
                        <td>{it.qty ?? 0}</td>
                        <td className="text-end">{it.rate ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>
          
          <div className="invoice-footer mt-4 pt-4 border-top">
            <p className="text-muted text-center">شكراً لاستخدامك خدماتنا</p>
            <p className="text-center">
              {/* <img
                src={qrImageUrl}
                alt="ZATCA QR Code"
                style={{ width: 128, height: 128, objectFit: "contain" }}
              /> */}
            </p>
          </div>
        </div>
        )}
      </ModalBody>
      <ModalFooter className="border-0">
        <Button color="secondary" onClick={toggle}>
          إغلاق
        </Button>
        {/* <Button
          color="danger"
          onClick={createCreditNote}
          disabled={refunding || !erpInvoice || transaction?.refunded}
        >
          {refunding ? <Spinner size="sm" className="me-1" /> : <FiDollarSign className="me-1" />}
          {transaction?.refunded ? "تم الاسترجاع" : "استرجاع المبلغ"}
        </Button> */}
        <Button color="success" onClick={downloadPDF}>
          <FiDownload className="me-1" />
          تحميل PDF
        </Button>
      </ModalFooter>
    </Modal>
  );
};
export default TransactionsHistory;